// Phase 31 Plan 31-03 Task 1 — AnchorPlacementOverlay.
//
// Pixi layer above LetterLayer that renders a Sun-Yellow border + 1.2× scale-
// pulse on every eligible cell (UI-SPEC §8 — eligibility = is_anchor false ∧
// is_hollow false ∧ letter ≠ null ∧ no Phase-B piece, via the shared
// isEligibleAnchorCell predicate; ARCA-599). Tap → POST /place-anchor via the
// Plan 31-01 wrapper. Optimistic UI: the tapped cell shows a spinner-style
// indicator but NEVER renders a full Anchor sprite (T-31-06 mitigation —
// real Anchor render waits for the next PlayerView snapshot).
//
// Mount gate: `playerView.requires_anchor_placement === true` (server-set).
// Returns null when the flag is false. Server clears the flag atomically with
// a successful POST, so the next PlayerView frame unmounts the overlay.
//
// CRITICAL — Pitfall 5 lock (P5): Tweedle integration uses `Group.shared`
// exclusively. NEVER instantiate a private Group. The shared group is ticked
// by the LetterLayer's installTweedleTickerDriver — we also install here so
// the overlay works in isolation (e.g., if Chess view is active and
// LetterLayer is unmounted). installTweedleTickerDriver is idempotent
// (Plan 30-01).
//
// B12 firewall: this component reads state EXCLUSIVELY from the `playerView`
// prop. It NEVER consults a global store, NEVER inspects window.location,
// NEVER falls back to URL parameters. The single trust boundary is the prop
// the parent (MatchScreen) hands down.

import { useStore } from '@nanostores/react'
import { useApplication } from '@pixi/react'
import type { Graphics } from 'pixi.js'
import { Rectangle } from 'pixi.js'
import {
  type JSX,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useIntl } from 'react-intl'
import { Easing, Group, Tween } from 'tweedle.js'
import { isEligibleAnchorCell } from '../../game/anchor-eligibility'
import {
  boardRows,
  CELL_SIZE_PX,
  cellIdxToScreen,
} from '../../game/layers/board-layout'
import type { PlayerView } from '../../game/PlayerViewSchema'
import { installTweedleTickerDriver } from '../../game/theme/tweedle-bootstrap'
import { resolveWordField } from '../../game/wire-grids'
import { placeAnchor } from '../../net/place-anchor'
import { $reducedMotion } from '../../stores/motion'
import { useAnnounce } from '../components/useAnnounce'

/** Sun-Yellow accent — UI-SPEC §3 token --color-cta-primary (hex form for Pixi). */
const SUN_YELLOW_HEX = 0xffc53d

/** Scale-pulse duration (one half-cycle 1.0 → 1.2). Full yoyo period = 1000ms. */
const PULSE_HALF_MS = 500

export interface AnchorPlacementOverlayProps {
  readonly playerView: PlayerView
  readonly matchId: string
  readonly yourSlot: 0 | 1
  /**
   * Optional callback fired on a placeAnchor rejection so the parent
   * (MatchScreen) can wire the existing submitError banner + assertive
   * announce flow. The overlay still calls useAnnounce assertive itself so
   * the screen-reader announcement is guaranteed even when no parent is
   * listening.
   */
  readonly onPlaceFailure?: (error: unknown) => void
  /**
   * Optional placement injection. When provided, a tap routes the chosen
   * `cell_idx` to this callback INSTEAD of the network `placeAnchor` POST —
   * the seam the canvas-driven tutorial uses to route placement to its
   * client-only runner (DEC-021: the tutorial never reaches a Match DO). When
   * omitted, the overlay keeps its default server-authoritative POST path.
   */
  readonly onPlace?: (cellIdx: number) => Promise<void> | void
  /**
   * ANCHOR-08 (ARCA-143 / ARCA-120) — fired after a placement succeeds (either
   * path) so the parent (MatchScreen) can latch a visible "Anchor placed!"
   * confirmation beat in the AnchorPlacementPrompt before the next server
   * snapshot unmounts this overlay. Independent of the SR announce below.
   */
  readonly onPlaceSuccess?: (cellIdx: number) => void
}

export function AnchorPlacementOverlay({
  playerView,
  matchId,
  yourSlot,
  onPlaceFailure,
  onPlace,
  onPlaceSuccess,
}: AnchorPlacementOverlayProps): JSX.Element | null {
  // P5 lock: install ticker driver once per Pixi Application. Idempotent.
  const application = useApplication() as { app?: { ticker?: unknown } }
  useEffect(() => {
    const ticker = application?.app?.ticker as
      | Parameters<typeof installTweedleTickerDriver>[0]
      | undefined
    if (!ticker) return undefined
    const teardown = installTweedleTickerDriver(ticker)
    return teardown
  }, [application])

  const reducedMotion = useStore($reducedMotion)
  const announce = useAnnounce()
  const intl = useIntl()
  const [pendingCellIdx, setPendingCellIdx] = useState<number | null>(null)

  // ARCA-749 (WS3) — anchors place on the WORD field (decoupled from the chess
  // grid). Reading word_field here also makes the ARCA-599 piece exclusion moot:
  // word cells carry no piece columns, so an anchor and a chess piece on the
  // same cell_idx live on separate surfaces and cannot collide.
  const wordField = resolveWordField(playerView)
  const eligibleCells = useMemo(
    // Shared with the DOM MatchGrid tap/keyboard gate so the visible Sun-Yellow
    // targets and the actually-tappable cells can never disagree (ARCA-599).
    () => wordField.filter(isEligibleAnchorCell),
    [wordField],
  )

  const handleTap = useCallback(
    async (cellIdx: number): Promise<void> => {
      // Ignore taps while a placement is in flight (single-shot per gate).
      if (pendingCellIdx !== null) return
      setPendingCellIdx(cellIdx)
      try {
        // DI seam (tutorial): route placement to the injected handler when
        // present; otherwise POST to the server (the live-match default).
        if (onPlace !== undefined) {
          await onPlace(cellIdx)
        } else {
          await placeAnchor(matchId, { cell_idx: cellIdx })
        }
        // ANCHOR-07 — confirm the placement to SR/keyboard players (the Pixi
        // optimistic dot is the sighted cue).
        announce(
          intl.formatMessage({ id: 'match.anchor_placement.placed_announce' }),
          'polite',
        )
        // ANCHOR-08 — latch the sighted "Anchor placed!" confirmation beat in
        // the parent's prompt so the success is visible even if the next
        // PlayerView snapshot unmounts this overlay almost immediately.
        onPlaceSuccess?.(cellIdx)
        // Keep optimistic state — the next PlayerView snapshot will arrive
        // with `requires_anchor_placement === false` and the overlay will
        // unmount atomically (server-authority — Phase 26 D-LOCK).
      } catch (error) {
        setPendingCellIdx(null)
        onPlaceFailure?.(error)
        announce(
          intl.formatMessage({ id: 'match.error.submit_failed' }),
          'assertive',
        )
      }
    },
    [
      pendingCellIdx,
      matchId,
      onPlace,
      onPlaceFailure,
      onPlaceSuccess,
      announce,
      intl,
    ],
  )

  if (!playerView.requires_anchor_placement) return null

  return (
    <pixiContainer
      label="AnchorPlacementOverlay"
      data-pixi-label="AnchorPlacementOverlay"
    >
      {eligibleCells.map((cell) => {
        const { screen_x, screen_y } = cellIdxToScreen(
          cell.cell_idx,
          yourSlot,
          boardRows(wordField.length),
        )
        return (
          <EligibleCellOverlay
            key={`anchor-eligible-${cell.cell_idx}`}
            cellIdx={cell.cell_idx}
            x={screen_x * CELL_SIZE_PX}
            y={screen_y * CELL_SIZE_PX}
            reducedMotion={reducedMotion}
            pending={pendingCellIdx === cell.cell_idx}
            onTap={handleTap}
          />
        )
      })}
    </pixiContainer>
  )
}

interface EligibleCellOverlayProps {
  readonly cellIdx: number
  readonly x: number
  readonly y: number
  readonly reducedMotion: boolean
  readonly pending: boolean
  readonly onTap: (cellIdx: number) => void
}

function EligibleCellOverlay({
  cellIdx,
  x,
  y,
  reducedMotion,
  pending,
  onTap,
}: EligibleCellOverlayProps): JSX.Element {
  const containerRef = useRef<{ scale?: { x: number; y: number } } | null>(null)
  const tweenRef = useRef<Tween<{ x: number; y: number }> | null>(null)

  useEffect(() => {
    if (reducedMotion) return undefined
    // 1.0 ↔ 1.2 sine over 1000ms (yoyo). Stagger per cellIdx to break sync-
    // flash patterns (Pitfall 5 lock — Group.shared is the SHARED group, not
    // a privately-instantiated Group).
    const target = containerRef.current?.scale
    if (!target) return undefined
    const tween = new Tween(target)
      .to({ x: 1.2, y: 1.2 }, PULSE_HALF_MS)
      .easing(Easing.Sinusoidal.InOut)
      .yoyo(true)
      .repeat(Number.POSITIVE_INFINITY)
      .delay((cellIdx % 8) * 50)
    Group.shared.add(tween)
    tween.start(Group.shared.now())
    tweenRef.current = tween
    return () => {
      tween.stop()
      tweenRef.current = null
    }
  }, [reducedMotion, cellIdx])

  const handlePointerDown = useCallback(() => {
    onTap(cellIdx)
  }, [cellIdx, onTap])

  // Pivot at the cell CENTER so the 1.0 ↔ 1.2 scale-pulse breathes
  // symmetrically outward instead of sliding toward the bottom-right.
  // Default pivot (0,0) made the scaled border grow only down + right, and
  // with the per-cell phase stagger (50ms × cellIdx % 8) that produced a
  // very visible "second grid sliding diagonally back and forth" moiré over
  // the static cell borders during the T2/T3 anchor-placement turns. The
  // position offset compensates so the cell renders in the same place at
  // scale 1.0; the hitArea (local coords) still covers the cell exactly.
  const HALF = CELL_SIZE_PX / 2
  return (
    <pixiContainer
      ref={containerRef as never}
      x={x + HALF}
      y={y + HALF}
      pivot={{ x: HALF, y: HALF }}
      eventMode="static"
      cursor="pointer"
      hitArea={new Rectangle(0, 0, CELL_SIZE_PX, CELL_SIZE_PX)}
      onPointerDown={handlePointerDown}
      // happy-dom test surrogate hooks — these data attributes are dropped by
      // the @pixi/react reconciler in production (it only consumes Pixi-known
      // props) but in happy-dom they surface on the lowercase host element so
      // the contract tests can query them.
      data-eligible-cell-idx={cellIdx}
      data-pending={pending ? 'true' : 'false'}
    >
      <pixiGraphics
        draw={(g: Graphics) => {
          g.clear()
          // 3px Sun-Yellow line, inset 2px from cell edge per UI-SPEC §8.
          g.rect(2, 2, CELL_SIZE_PX - 4, CELL_SIZE_PX - 4).stroke({
            width: 3,
            color: SUN_YELLOW_HEX,
            alpha: 1,
          })
          // Optimistic UI: when pending, confirm the placement on the cell the
          // player tapped — a Sun-Yellow full-cell wash PLUS a bolder top-right
          // disc. This is the distinct "you placed HERE" cue (ANCHOR-08 /
          // ARCA-143), well beyond the old 4px dot, and it stays visible for the
          // brief window before the next PlayerView snapshot unmounts the
          // overlay. Still NOT a full Anchor sprite (T-31-06 mitigation) — the
          // real Anchor render arrives on that snapshot.
          if (pending) {
            g.rect(2, 2, CELL_SIZE_PX - 4, CELL_SIZE_PX - 4).fill({
              color: SUN_YELLOW_HEX,
              alpha: 0.3,
            })
            g.circle(CELL_SIZE_PX - 12, 12, 6).fill({
              color: SUN_YELLOW_HEX,
              alpha: 0.95,
            })
          }
        }}
      />
    </pixiContainer>
  )
}
